import { Score, ScoreVariation } from "src/types";
import { ScoreType } from "src/api/client-api/scoreType/useGetAllScoreType";
import { MainScore } from "src/api/client-api/olapScores/olapScoresSchema";

const getScoreType = (type: ScoreType["type"], scoreTypes?: ScoreType[]): ScoreType | undefined => {
    return scoreTypes?.find((scoreType: ScoreType) => scoreType.type === type);
};

const isScoreDisplayable = (scoreId: number, scoresFromSure?: Score[]) => {
    if (scoresFromSure === undefined) return true;

    return scoresFromSure.some((score: Score) => score.id === scoreId);
};

const isVariationDisplayable = (variationId: number, scoresFromSure?: Score[]) => {
    if (scoresFromSure === undefined) return true;

    return scoresFromSure.some((score: Score) =>
        score.variations.some((variation: ScoreVariation) => variation.id === variationId),
    );
};

export const getScoreIdsByType = (type: ScoreType["type"], scoreTypes?: ScoreType[], scores?: Score[]): number[] => {
    const scoresIds = getScoreType(type, scoreTypes)?.scoreIds ?? [];

    return scoresIds.filter((scoreId) => isScoreDisplayable(scoreId, scores));
};

export const getSubscoreIdsByType = (type: ScoreType["type"], scoreTypes?: ScoreType[], scores?: Score[]): number[] => {
    const scoresIds = getScoreType(type, scoreTypes)?.subscoreIds ?? [];

    return scoresIds.filter((scoreId) => isScoreDisplayable(scoreId, scores));
};

export const getVariationIdsByType = (
    type: ScoreType["type"],
    scoreTypes?: ScoreType[],
    scores?: Score[],
): number[] => {
    const variationsIds = getScoreType(type, scoreTypes)?.variationIds ?? [];

    return variationsIds.filter((variationId) => isVariationDisplayable(variationId, scores));
};

export const getActionPlanScoreIds = (scoreType: ScoreType | undefined): number[] =>
    scoreType?.actionPlanScoreIds ?? scoreType?.scoreIds ?? [];

export const getActionPlanScoreIdsByType = (type: ScoreType["type"], scoreTypes?: ScoreType[]): number[] =>
    getActionPlanScoreIds(getScoreType(type, scoreTypes));

export const getVariationOrderByType = (type: ScoreType["type"], scoreTypes?: ScoreType[]): number[] =>
    getScoreType(type, scoreTypes)?.variationOrder ?? [];

export const orderVariations = <Variation extends { id: number }>(
    variations: Variation[],
    variationOrder: number[],
): Variation[] => {
    const rankOf = (variation: Variation): number => {
        const rank = variationOrder.indexOf(variation.id);

        return rank === -1 ? variationOrder.length : rank;
    };

    return variations.toSorted((left, right) => rankOf(left) - rankOf(right));
};

export const getDefaultVariationIdsByType = (
    type: ScoreType["type"],
    scoreTypes?: ScoreType[],
    scores?: Score[],
): number[] => {
    const variationsIds = getScoreType(type, scoreTypes)?.defaultVariationIds ?? [];

    return variationsIds.filter((variationId) => isVariationDisplayable(variationId, scores));
};

export const getDisplayValueForScore = (value: number, precision: number): string | number => {
    const roundedValue = Number(value.toFixed(precision));
    if (roundedValue === 0 && value !== 0) {
        return `< 0.${"0".repeat(precision)}5`;
    }
    return roundedValue;
};

export const getSubscoreOfDisplayModeFromScoreOverview = (score: MainScore | null | undefined, displayMode: string) => {
    if (score === null || score === undefined) {
        return null;
    }

    const foundSubscore =
        Object.entries(score.subscores ?? {}).flatMap(([_subscoreId, subscore]) => {
            if (subscore.displayMode !== displayMode) return [];

            return [subscore];
        }) ?? [];

    if (foundSubscore.length > 0) {
        return foundSubscore[0];
    }

    return null;

    // const keys = Object.keys(score?.subscores ?? {}).filter((subscoreId) => {
    //     return score.subscores?.[parseInt(subscoreId)].type === type;
    // });

    // const key = keys.length > 0 ? keys[0] : null;
    // if (key === null) {
    //     return null;
    // }

    // return score.subscores?.[parseInt(key)] ?? null;
};

export const isDisplayedAsMainScore = (score: Score) => {
    return score.displayMode === "climate_change";
};

const SURE_ELIGIBLE_ENTITY_TYPE_BY_SCORE_TYPE: Partial<Record<ScoreType["type"], string>> = {
    company: "organization",
    product: "product",
    ingredient: "ingredient",
    packaging: "packaging",
    transport: "transport",
};

export const hasNoEligibleEntityType = (score: Pick<Score, "eligibleEntityTypes">): boolean =>
    (score.eligibleEntityTypes ?? []).length === 0;

export const isScoreEligibleForScoreType = (
    score: Pick<Score, "eligibleEntityTypes">,
    type: ScoreType["type"],
): boolean => {
    // ponytail: unqualified SURE scores are listed in every section until all scores are qualified in sure-admin, then drop this branch
    if (hasNoEligibleEntityType(score)) return true;

    const sureEntityType = SURE_ELIGIBLE_ENTITY_TYPE_BY_SCORE_TYPE[type];
    return sureEntityType !== undefined && (score.eligibleEntityTypes ?? []).includes(sureEntityType);
};
